import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_PATHS, INSTALLATIONS_TABLE_COLUMNS_CONFIG, XEMEX_OID } from '@core/constants';
import { canUseInstallationFeature } from '@core/helpers/installation-access.helper';
import { browserZone, zonedInstant } from '@core/helpers/slot-series.helper';
import { Installation, InstallationFilter, Preset, getUserActivePartner } from '@core/models';
import { CompanyService, LocationService } from '@core/services';
import { InstallationProfileService } from '@core/services/installation-profile.service';
import { locationFilterGroup } from '@core/utils/location-filter-group.util';
import { partnerFilterGroup } from '@core/utils/partner-filter-group.util';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import { FilledButtonComponent } from '@custom-components/general/button/filled-button/filled-button.component';
import { TableComponent } from '@custom-components/general/table/table.component';
import { FilterGroup, TableFilterBarComponent } from '@custom-components/general/table-filter-bar';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';
import { AuthStore } from '@root/app/store/auth.store';
import { InstallationStore } from '@root/app/store/installation.store';
import { UserStore } from '@root/app/store/user.store';
import {
  InstallationCreateDialogComponent,
  InstallationCreateDialogData,
  installationCreateDialogConfig,
} from '../../components/installation-create-dialog/installation-create-dialog.component';

const COMMISSIONING_OPTIONS: NonNullable<InstallationFilter['commissioning']>[] = [
  'complete',
  'incomplete',
];

// The newest readout of a bound device, shown as the device page shows that device's last
// readout: in the browser's zone.
export function lastDataLabel(lastDataAt: string | null): string {
  return lastDataAt ? zonedInstant(lastDataAt, browserZone(), 'D MMM YYYY, HH:mm') : '–';
}

@Component({
  selector: 'xemex-installations-page',
  imports: [
    FilledButtonComponent,
    PageBodyComponent,
    PageHeaderComponent,
    TableComponent,
    TableFilterBarComponent,
    TranslateModule,
  ],
  providers: [InstallationStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installations-page.component.html',
})
export class InstallationsPageComponent {
  readonly installationStore = inject(InstallationStore);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);
  private readonly profileService = inject(InstallationProfileService);
  private readonly companyService = inject(CompanyService);
  private readonly locationService = inject(LocationService);
  private readonly dialog = inject(MatDialog);

  protected readonly isXemexUser = computed(
    () => getUserActivePartner(this.userStore.user())?.partnerOid === XEMEX_OID,
  );

  protected readonly canCreate = computed(() =>
    canUseInstallationFeature(
      this.userStore.user(),
      this.authStore.permissions(),
      'create_installation',
    ),
  );

  protected readonly columns = computed(() =>
    this.isXemexUser()
      ? INSTALLATIONS_TABLE_COLUMNS_CONFIG
      : INSTALLATIONS_TABLE_COLUMNS_CONFIG.filter(column => column.field !== 'partnerName'),
  );

  protected readonly breadcrumbs: BreadcrumbItem[] = [
    { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
    { label: this.translate.instant('sidebarMenu.installations') },
  ];

  protected readonly tableData = computed(
    () =>
      this.installationStore
        .installations()
        .installations.map(installation => this.toRow(installation)) as unknown as Record<
        string,
        unknown
      >[],
  );
  protected readonly totalCount = computed(
    () => this.installationStore.installations().numberOfItems,
  );

  // The profile pill appears once the presets are known; a failed read leaves it out.
  private readonly presets = toSignal<Preset[] | null>(
    this.profileService.presets().pipe(catchError(() => of(null))),
    { initialValue: null },
  );

  protected readonly filterGroups = computed<FilterGroup[]>(() => {
    const presets = this.presets();
    return [
      ...(this.isXemexUser()
        ? [
            partnerFilterGroup(
              this.companyService,
              this.translate.instant('installations.filter.partner'),
              this.translate.instant('general.placeholderInput'),
            ),
          ]
        : []),
      ...(presets
        ? [
            {
              key: 'preset',
              label: this.translate.instant('installations.filter.profile'),
              selection: 'single' as const,
              options: presets.map(preset => preset.displayName),
            },
          ]
        : []),
      {
        key: 'commissioning',
        label: this.translate.instant('installations.filter.commissioning'),
        selection: 'single',
        options: COMMISSIONING_OPTIONS.map(option =>
          this.translate.instant(`installations.commissioning.${option}`),
        ),
      },
      ...(this.locationFiltered()
        ? [
            locationFilterGroup(
              this.locationService,
              this.translate.instant('installations.filter.location'),
              this.translate.instant('installations.filter.locationPlaceholder'),
            ),
          ]
        : []),
    ];
  });

  protected readonly rowLink = (row: Record<string, unknown>) => [
    '/',
    APP_PATHS.Installations,
    String(row['installationOid']),
    'general',
  ];

  // The filters and the search term live in the query string, so a reload, the back button and a
  // shared link keep them. The filter bar reads them once; after that it reports every change.
  protected readonly initialSearch = this.route.snapshot.queryParamMap.get('search') ?? '';
  private readonly urlFilters = this.filtersFromUrl();

  // The location pill is there only while a location is set, which a link from the location page
  // does; clearing its chip takes the pill away.
  private readonly locationFiltered = signal(!!this.urlFilters['location']);
  private readonly urlPreset = this.route.snapshot.queryParamMap.get('preset') || undefined;

  // The profile chip shows the preset's name, which is known only once the presets are.
  protected readonly initialFilters = computed<Record<string, string[]>>(() => {
    const displayName = this.presets()?.find(
      preset => preset.preset === this.urlPreset,
    )?.displayName;
    return displayName ? { ...this.urlFilters, preset: [displayName] } : this.urlFilters;
  });

  private search = this.initialSearch;
  private filters: Record<string, string[]> = this.urlFilters;
  private presetCode = this.urlPreset;

  constructor() {
    this.applyFilters();

    // A code in the query string that is not a preset would leave an empty list with no chip to clear.
    effect(() => {
      const presets = this.presets();
      untracked(() => {
        if (presets && this.presetCode && !presets.some(p => p.preset === this.presetCode)) {
          this.presetCode = undefined;
          this.applyFilters();
          this.writeUrl();
        }
      });
    });
  }

  protected openCreateDialog(): void {
    this.dialog.open<InstallationCreateDialogComponent, InstallationCreateDialogData>(
      InstallationCreateDialogComponent,
      installationCreateDialogConfig({}),
    );
  }

  protected onSearchChange(query: string): void {
    this.search = query;
    this.applyFilters();
    this.writeUrl();
  }

  protected onFilterChange(filters: Record<string, string[]>): void {
    this.filters = filters;
    this.locationFiltered.set(!!filters['location']?.length);
    this.presetCode = this.presets()?.find(
      preset => preset.displayName === filters['preset']?.[0],
    )?.preset;
    this.applyFilters();
    this.writeUrl();
  }

  protected onPageChange(event: PageEvent): void {
    this.installationStore.loadPage(event.pageIndex);
  }

  private applyFilters(): void {
    this.installationStore.applyFilter({
      name: this.search || undefined,
      partnerOid: this.filters['partner']?.[0] || undefined,
      commissioning: this.commissioningFilter(),
      preset: this.presetCode,
      locationId: Number(this.filters['location']?.[0]) || undefined,
    });
  }

  // The select pill carries the translated option, the query string the value itself.
  private commissioningFilter(): InstallationFilter['commissioning'] {
    const label = this.filters['commissioning']?.[0];
    return COMMISSIONING_OPTIONS.find(
      option => this.translate.instant(`installations.commissioning.${option}`) === label,
    );
  }

  private filtersFromUrl(): Record<string, string[]> {
    const params = this.route.snapshot.queryParamMap;
    const commissioning = COMMISSIONING_OPTIONS.find(
      option => option === params.get('commissioning'),
    );
    const partner = this.isXemexUser() ? params.get('partner') : null;
    // `locationId` is the name the list read before the filters moved into the URL.
    const location = params.get('location') ?? params.get('locationId');
    const filters: Record<string, string[]> = {};
    if (commissioning) {
      filters['commissioning'] = [
        this.translate.instant(`installations.commissioning.${commissioning}`),
      ];
    }
    if (partner) filters['partner'] = [partner];
    if (Number(location)) filters['location'] = [String(Number(location))];
    return filters;
  }

  // `status` and `ldn` are no longer filters; nulling them drops them from an older link.
  private writeUrl(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        commissioning: this.commissioningFilter() ?? null,
        partner: this.filters['partner']?.[0] ?? null,
        preset: this.presetCode ?? null,
        location: this.filters['location']?.[0] ?? null,
        search: this.search || null,
        status: null,
        ldn: null,
        locationId: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private toRow(installation: Installation): Record<string, unknown> {
    return {
      installationOid: installation.installationOid,
      name: installation.name,
      partnerName: installation.partnerName ?? '',
      profile: installation.presetName ?? this.translate.instant('installations.profile.custom'),
      commissioning: this.commissioningLabel(installation.commissioningComplete),
      commissioningState: installation.commissioningComplete ? 'success' : 'attention',
      lastData: lastDataLabel(installation.lastDataAt),
    };
  }

  private commissioningLabel(complete: boolean | null): string {
    if (complete === null) {
      return '';
    }
    return this.translate.instant(
      complete ? 'installations.commissioning.complete' : 'installations.commissioning.incomplete',
    );
  }
}
